import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, generatePath, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '@/components/Button';
import {
	CfCaptchaWidget,
	useCfTurnstileChallenge
} from '@/components/CfCaptchaWidget';
import ErrorMessage from '@/components/form/ErrorMessage';
import Input from '@/components/form/Input';
import {
	useGetErrorMessage,
	useMediaQuery,
	useShallowEqualSelector
} from '@/hooks';
import { useTransactionId } from '@/hooks/editor';
import { useOauthErrors } from '@/hooks/oauth';
import { useLanguage } from '@/hooks/user';
import Account from '@/icons/account.svg';
import Key from '@/icons/key.svg';
import userModel from '@/models/user';
import routes from '@/routes';
import {
	AUTH_MODAL_SCOPES,
	AUTH_MODAL_TYPES,
	closeAuthModal,
	openAuthModal
} from '@/store/profile';
import { fetchUser } from '@/store/profile/thunks';
import { delay } from '@/utils';

import Description from './components/Description';
import Divider from './components/Divider';
import LevelPassword from './components/LevelPassword';
import OAuthButtons from './components/OAuthButtons';
import TextLink from './components/TextLink';
import Title from './components/Title';
import { DEFAULT_REDIRECT_ROUTE } from './components/constants';

function SignUp({ isFastCheckout }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const mdDown = useMediaQuery('mdDown');
	const navigate = useNavigate();
	const language = useLanguage();
	const id = useTransactionId();

	const [errorMessage, setErrorMessage] = useState('');
	const [loading, setLoading] = useState(false);

	const getErrorMessage = useGetErrorMessage();

	const { authModalRedirect, authModalScope } = useShallowEqualSelector(
		state => ({
			authModalRedirect: state.profile.authModalRedirect,
			authModalScope: state.profile.authModalScope
		})
	);
	const { clearOauthError } = useOauthErrors();
	const {
		cfChallengeToken,
		cfChallengeCompleted,
		cfIdempotencyKey,
		triggerChallenge,
		setCfIdempotencyKey
	} = useCfTurnstileChallenge({
		action: 'register'
	});

	const schema = Yup.object({
		email: Yup.string().email().min(3).max(50).required(),
		password: Yup.string().required()
	});

	const methods = useForm({
		defaultValues: { email: '', password: '' },
		mode: 'onBlur',
		resolver: yupResolver(schema)
	});

	const { email, password } = methods.watch();

	useEffect(() => {
		if (email.includes('@')) {
			triggerChallenge();
		}
	}, [email]);

	const handleClose = () => {
		dispatch(closeAuthModal());
		clearOauthError();
	};

	async function onSubmit(values) {
		setLoading(true);
		setErrorMessage('');

		try {
			await userModel.create({
				...values,
				language,
				cfChallengeToken,
				cfIdempotencyKey,
				googleAdId: localStorage.getItem('googleAdId') || undefined
			});

			localStorage.removeItem('googleAdId');

			await dispatch(fetchUser()).unwrap();

			if (isFastCheckout && id) {
				navigate(generatePath(routes.fastCheckout, { id }), {
					state: { scope: authModalScope }
				});
			} else {
				navigate(authModalRedirect || DEFAULT_REDIRECT_ROUTE);
			}

			await delay(200);
			dispatch(closeAuthModal());
		} catch (error) {
			if (error?.data?.cfIdempotencyKey) {
				setCfIdempotencyKey(error.data.cfIdempotencyKey);
			}
			setErrorMessage(getErrorMessage(error));
		} finally {
			setLoading(false);
		}
	}

	const isFontPoppins =
		mdDown && Object.values(AUTH_MODAL_SCOPES).includes(authModalScope);
	const textTitle = Object.values(AUTH_MODAL_SCOPES).includes(authModalScope)
		? t(`authModal.signUp.${authModalScope}.title`)
		: t('authModal.signUp.title');

	const textDescription = Object.values(AUTH_MODAL_SCOPES).includes(
		authModalScope
	)
		? t(`authModal.signUp.${authModalScope}.description`)
		: t('authModal.signUp.description');

	return (
		<>
			<Title className={isFontPoppins ? 'font-sans' : ''}>{textTitle}</Title>
			<Description>{textDescription}</Description>

			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<div className="mb-3 mt-4 grid gap-2 md:mb-5 md:mt-6 md:gap-4">
						<Input
							StartIcon={Account}
							name="email"
							placeholder={t('authModal.emailPlaceholder')}
						/>
						<Input
							StartIcon={Key}
							name="password"
							type="password"
							placeholder={t('common.password')}
						/>

						<LevelPassword password={password} />

						<CfCaptchaWidget />
					</div>

					<Button
						type="submit"
						className="w-full"
						loading={loading}
						disabled={!cfChallengeCompleted}
					>
						{t('authModal.createAccount')}
					</Button>

					{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

					<Divider className="my-2 md:my-5" />

					<OAuthButtons
						type="create"
						fastCheckout={isFastCheckout}
						authTrigger={
							authModalScope ? `${authModalScope}-register` : null
						}
					/>
				</form>

				<TextLink
					disabled={loading}
					className="mb-4"
					text={t('authModal.signUp.alreadyHaveAccount')}
					linkText={t('authModal.signIn')}
					onClick={() =>
						dispatch(openAuthModal({ type: AUTH_MODAL_TYPES.LOGIN }))
					}
				/>

				<p className="text-center text-[10px] leading-4 text-grey-500 md:text-xs md:leading-5">
					<Trans
						i18nKey="authModal.signUp.disclaimer"
						components={{
							anchor1: (
								<Link
									className="ml-1 text-black"
									to={routes.terms}
									onClick={handleClose}
								/>
							),
							anchor2: (
								<Link
									className="ml-1 text-black"
									to={routes.privacyPolicy}
									onClick={handleClose}
								/>
							)
						}}
					/>
				</p>
			</FormProvider>
		</>
	);
}

export default SignUp;
